/**
 * CCO › Relatórios › Semana, quinzena e mês: comportamento da rede por setor com os destaques do gestor (US-3405) — Nexora Grid.
 *
 * Criado em: 26/10/2026 · Última modificação: 26/10/2026 · Versão: 1.0.0
 *
 * O último período fechado de cada tipo (semana seg–dom, quinzena 1–15 / 16–fim, mês), gerado pela plataforma a partir
 * das visões da US-3004: por setor, cada medida com a variação sobre o período anterior e sobre a referência; os
 * destaques calculados (variação de 10% ou mais, pressão fora da NBR, período incompleto) com a fonte; o resumo do
 * gestor (texto, sem números novos) e a ronda do gestor no período.
 */
import { useEffect, useState } from "react";
import { api, ErroApi } from "../api";
import { useCco } from "./contexto";

type Tipo = "semana" | "quinzena" | "mes";
type Metrica = { valor: number | null; anterior: number | null; variacao_anterior_pct: number | null; referencia: number | null;
  variacao_referencia_pct: number | null };
type Setor = { setor: string; completo: boolean; dias_com_dado: number; pct_tempo_nbr: number | null; metricas: Record<string, Metrica> };
type Relatorio = { tipo: Tipo; periodo_inicio: string; periodo_fim: string; referencia: string; setores: Setor[];
  destaques: { setor: string; texto: string; fonte: string }[]; fontes: string[]; texto_gestor: string; custo_usd: number;
  ronda?: { gatilhos: number; encerrados: number; falsos_alertas: number; feedback: { taxa_utilidade_pct: number | null } } };

const NOMES: Record<Tipo, string> = { semana: "Semana", quinzena: "Quinzena", mes: "Mês" };
const METRICAS: [string, string, number][] = [
  ["pressao_media_mca", "Pressão média (mca)", 1], ["volume_distribuido_m3_dia", "Volume/dia (m³)", 0],
  ["vazao_minima_noturna_m3h", "Vazão mín. noturna (m³/h)", 1], ["nivel_medio_m", "Nível médio (m)", 2],
  ["energia_kwh_dia", "Energia/dia (kWh)", 0], ["alarmes_dia", "Alarmes/dia", 1],
];
const fmt = (v: number | null | undefined, casas: number) =>
  v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { maximumFractionDigits: casas, minimumFractionDigits: casas });
const variacao = (v: number | null) => (v === null ? "" : ` (${v > 0 ? "+" : ""}${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%)`);
const dataBR = (iso: string) => iso.split("-").reverse().join("/");

export function RelatoriosPeriodo() {
  const { empresa } = useCco();
  const [tipo, setTipo] = useState<Tipo>("semana");
  const [relatorio, setRelatorio] = useState<Relatorio | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    if (!empresa) return;
    setRelatorio(null);
    setAviso(null);
    api<Relatorio>(`/v1/relatorios/periodo?empresa=${empresa}&tipo=${tipo}`).then(setRelatorio)
      .catch((x) => setAviso(x instanceof ErroApi && x.status === 404
        ? `O relatório ${tipo === "mes" ? "do mês" : `da ${NOMES[tipo].toLowerCase()}`} ainda não foi gerado: ele sai quando o período fecha e há dados no hub.`
        : x instanceof ErroApi ? x.message : "Falha de rede."));
  }, [empresa, tipo]);

  return (
    <section className="nx-cartao relatorios-periodo" aria-label="Relatórios de período">
      <h3>Semana, quinzena e mês</h3>
      <div className="sub-abas" role="group" aria-label="Período">
        {(Object.keys(NOMES) as Tipo[]).map((t) => (
          <button key={t} type="button" className={`nx-botao${tipo === t ? " ativo" : ""}`} aria-pressed={tipo === t} onClick={() => setTipo(t)}>{NOMES[t]}</button>
        ))}
      </div>
      {aviso && <p className="nx-suave" role="status">{aviso}</p>}
      {relatorio && (
        <>
          <p className="nx-suave">{dataBR(relatorio.periodo_inicio)} a {dataBR(relatorio.periodo_fim)} · comparado com o período anterior e com a {relatorio.referencia}</p>
          {relatorio.texto_gestor && (
            <div className="nx-cartao resumo-gestor" role="region" aria-label="Destaques do gestor">
              <h4>Destaques do gestor</h4>
              <div style={{ whiteSpace: "pre-wrap" }}>{relatorio.texto_gestor}</div>
              <small className="nx-suave">Escrito pelo gestor a partir dos números abaixo (US$ {relatorio.custo_usd.toFixed(4)}). Só sugestão.</small>
            </div>
          )}
          <h4>Destaques calculados</h4>
          <ul>{relatorio.destaques.map((d) => <li key={d.texto} title={d.fonte}>{d.texto}</li>)}
            {!relatorio.destaques.length && <li className="nx-suave">sem variações relevantes</li>}</ul>
          <div className="tabela-rolagem">
            <table className="tabela-composicao">
              <thead><tr><th>Setor</th>{METRICAS.map(([, rotulo]) => <th key={rotulo}>{rotulo}</th>)}<th>Na NBR (% do tempo)</th></tr></thead>
              <tbody>
                {relatorio.setores.map((s) => (
                  <tr key={s.setor}>
                    <td><b>{s.setor}</b>{!s.completo && <><br /><small className="nx-suave">incompleto ({s.dias_com_dado} dias)</small></>}</td>
                    {METRICAS.map(([chave, , casas]) => (
                      <td key={chave} className="nx-valor">{fmt(s.metricas[chave]?.valor, casas)}
                        <small className="nx-suave">{variacao(s.metricas[chave]?.variacao_anterior_pct ?? null)}</small></td>
                    ))}
                    <td className="nx-valor">{fmt(s.pct_tempo_nbr, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {relatorio.ronda && (
            <p className="nx-suave">Ronda do gestor no período: {relatorio.ronda.gatilhos} gatilho(s), {relatorio.ronda.encerrados} normalizado(s), {relatorio.ronda.falsos_alertas} falso(s) alerta(s)
              {relatorio.ronda.feedback.taxa_utilidade_pct !== null ? `, ${relatorio.ronda.feedback.taxa_utilidade_pct.toLocaleString("pt-BR")}% úteis` : ""}.</p>
          )}
          <p className="nx-suave">Fontes: {relatorio.fontes.join(" · ")}</p>
        </>
      )}
    </section>
  );
}
